/* KCI Event Tagging › Create KCI Event (KCI_API_DOC §3–4, mockup Steps 1–6).

   Admin-panel wizard: segmented WizardStepper on top, ONE step per card, and the
   Back / Next / Save action row INSIDE the card footer. Visual language matches the
   Escalation "Create Ticket" page (SectionCard header + tinted icon tile, gradient-
   blue primary CTA, radial page background) so it reads as part of the admin panel.

   An event always starts from a CP Intimation — advancing past Step 1 logs (or links)
   the intimation and returns the sourceIntimationId the event links to. Save creates
   the event via POST /kci/event. Courier list = shared cached courier-partner API.
   Backend-unbuilt controls (KCI_API_DOC §9) are shown disabled; bulk pincode upload is
   parsed client-side into the same pincodes array. */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormProvider, RHFSearchableSelectField, RHFMultipleSelectField } from '../../common/hook-form'
import { useNavigate } from 'react-router-dom'
import {
  Box, Grid, Stack, Typography, TextField, Autocomplete, Button, Chip,
  ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox, Radio, RadioGroup,
  Divider, CircularProgress, Tooltip, Stepper, Step, StepLabel, Alert,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import {
  useSuggestReasonMutation, useUploadAttachmentMutation, useCreateIntimationMutation,
  useMarkInformationalMutation, usePendingIntimationsQuery, useGeoStatesQuery,
  useGeoCitiesQuery, useGeoResolveMutation, useCreateEventMutation,
} from '../../redux/kciApiSlice'
import {
  REASON_CATEGORIES, reasonLabel, reasonColor, DIRECTIONS,
  GEO_TYPE, IMPACT_TYPES,
} from './kciConstants'
import { errMsg, sepOption, COURIER_LIST_FILTER, dedupeCouriers } from './kciUi'

// ── Merchant-panel "Create Forward Shipment" visual language (proship-web
//    styles/patterns + foundation tokens), replicated so KCI Create reads identically:
//    left vertical Stepper card + right form card + "How it works" banner. ──
const NAVY = '#212B36'
const MUTED = '#637381'
const SUBTLE = '#637381'
const BORDER = '#DFE3E8'
const WHITE = '#fff'
const BLUE = '#3783e7'            // merchant PRIMARY_BLUE
const PAGE_BG = '#F7F8FA'
const pageContainerSx = { bgcolor: PAGE_BG, minHeight: '100%', p: { xs: 1.5, md: 2.5 }, mx: { xs: -1.5, md: -2.5 }, mt: { xs: -1.5, md: -2.5 } }
const helpBannerSx = { display: 'flex', gap: 1, p: 1.5, mb: 2, borderRadius: '8px', bgcolor: '#f0f4ff', border: '1px solid #c5d8f6' }
const helpTextSx = { fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#455A64', lineHeight: 1.5 }
const sidebarCardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', p: 2 }
const stepperSx = {
  '& .MuiStepLabel-label': { fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 500, color: NAVY },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: 700, color: BLUE },
  '& .MuiStepLabel-label.Mui-completed': { color: SUBTLE },
  '& .MuiStepIcon-root.Mui-active': { color: BLUE },
  '& .MuiStepIcon-root.Mui-completed': { color: BLUE },
}
const formCardSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', p: { xs: 2.5, md: 3 },
  // Uniform 44px control height so every field lines up — single-line inputs, selects,
  // and the courier multi-select (one row of chips) all share the same height.
  '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB', minHeight: 44 },
  '& .MuiOutlinedInput-root.MuiInputBase-multiline': { minHeight: 44, alignItems: 'flex-start' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
  // Field text kept modest (12px) but the typing area padded so the field height
  // stays comfortable — height is driven by font-size + padding, not font alone.
  '& .MuiInputBase-input': { fontSize: 12, padding: '11px 12px' },
  '& .MuiInputBase-inputMultiline': { padding: 0 },
  '& .MuiFormHelperText-root': { fontSize: 10 },
  // Keep every label in the floated position (never resting inside), so fields in
  // the same row baseline-align — the date input is forced-floated by the browser,
  // this makes the neighbours match it. Notch kept open; placeholders stay visible.
  '& .MuiInputLabel-root': { fontSize: 12, transform: 'translate(12px, -8px) scale(0.85)' },
  '& .MuiOutlinedInput-notchedOutline legend': { maxWidth: '100%' },
  '& .MuiOutlinedInput-notchedOutline legend > span': { paddingRight: 4 },
  '& .MuiInputBase-input::placeholder': { opacity: 0.5 },
}
const stepTitleSx = { fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 18, color: NAVY, mb: 2 }
// flat blue primary (name kept as gradientBtnSx so existing refs need no edit)
const gradientBtnSx = {
  textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, py: 0.9,
  bgcolor: BLUE, color: '#fff', boxShadow: 'none',
  '&:hover': { bgcolor: '#2f6bd0', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: BLUE, opacity: 0.55, color: '#fff' },
}
const resetBtnSx = { textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.25, py: 0.9, color: MUTED, borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }
// Distinct "log only, no event" action — a clear tinted-blue alternative so it reads
// as its own path, not a dead grey button next to the primary Continue.
const infoOnlyBtnSx = {
  textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.25, py: 0.9,
  color: '#0369a1', borderColor: '#9ECBE8', bgcolor: '#EFF7FD',
  '&:hover': { bgcolor: '#E1EEFB', borderColor: '#0369a1' },
  '&.Mui-disabled': { opacity: 0.55 },
}
const toggleGroupSx = {
  gap: 0.75, flexWrap: 'wrap',
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid #E2E8F0 !important', borderRadius: '9px !important', mx: 0,
    textTransform: 'none', fontWeight: 600, fontSize: 12.5, color: '#64748b', px: 1.75, py: 0.65,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: 'rgba(32,101,209,.10) !important', color: `${BLUE} !important`, borderColor: `${BLUE} !important`,
    '&:hover': { bgcolor: 'rgba(32,101,209,.16) !important' },
  },
}
// minmax(0, 1fr) so a column never grows past half the container to fit wide
// content (e.g. a multi-select with several long courier labels) — keeps the page
// from stretching; overflow inside a cell is handled by the field itself.
const ROW2 = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }

// step key + stepper label + card-header title/sub/icon, one source of truth
const STEPS = [
  { key: 'intimation', label: 'CP Intimation', title: 'CP Intimation — Courier Partner Communication', sub: 'What the courier partner told us', icon: <MailOutlineIcon /> },
  { key: 'details', label: 'Event Details', title: 'Event Details', sub: 'Title, reason & reference', icon: <DescriptionOutlinedIcon /> },
  { key: 'scope', label: 'Direction & Impact', title: 'Direction & Impact', sub: 'Which shipments this applies to', icon: <LocalShippingOutlinedIcon /> },
  { key: 'geography', label: 'Geography', title: 'Geography Selection', sub: 'Where the disruption applies', icon: <PlaceOutlinedIcon /> },
  { key: 'duration', label: 'Duration', title: 'Duration', sub: 'When the event is active', icon: <EventOutlinedIcon /> },
  { key: 'notify', label: 'Notifications', title: 'Notifications & Merchant Visibility', sub: 'Who is informed', icon: <NotificationsNoneOutlinedIcon /> },
]
const LAST = STEPS.length - 1

// Day-granularity fields go to the API as plain yyyy-MM-dd. Do NOT append a UTC time:
// the server runs in IST and truncates to end-of-day there, so anything at or after
// 18:30Z (e.g. the old `T23:59:59.000Z`) lands on the NEXT IST day and the event silently
// runs a day longer than the picker showed.
const dayOnly = (d) => d || null
// sentinel for the "All couriers (Proship-wide)" dropdown entry → payload allCouriers: true
const ALL_PARENTS = '__ALL__'
const parseList = (s) => (s || '').split(/[\s,]+/).map((x) => x.trim()).filter(Boolean)
// Per-leg geography (pickup / drop). Fresh object each call so the two legs never share arrays.
const emptyLeg = () => ({ geoType: GEO_TYPE.CITY, cities: [], states: [], pinText: '', excludedText: '', resolved: null })
// One leg → its geography block for POST /kci/event (KCI_API_DOC §4.2, per-leg).
const legBody = (leg) => {
  if (leg.geoType === GEO_TYPE.PAN) return { geographyType: 'PAN_INDIA' }
  if (leg.geoType === GEO_TYPE.PIN) return { geographyType: 'PINCODE_LIST', pincodes: parseList(leg.pinText) }
  return {
    geographyType: leg.geoType,
    selectedStates: leg.geoType === GEO_TYPE.STATE ? leg.states : [],
    selectedCities: leg.geoType === GEO_TYPE.CITY ? leg.cities : [],
    excludedPincodes: parseList(leg.excludedText),
  }
}

export default function CreateKciEvent() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  // ── data sources ───────────────────────────────────────────────────────────
  // Courier list = the panel's shared cached courier-partner list (KCI_API_DOC §
  // integration notes: use the admin panel's existing courier-partner API, as
  // every other admin screen does). Returns cpAccountCode / name / parent.
  const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_FILTER)
  const { data: pending = [] } = usePendingIntimationsQuery({ limit: 50 })

  // ── mutations ──────────────────────────────────────────────────────────────
  const [suggestReason] = useSuggestReasonMutation()
  const [uploadAttachment, { isLoading: uploading }] = useUploadAttachmentMutation()
  const [createIntimation, { isLoading: loggingIntimation }] = useCreateIntimationMutation()
  const [markInformational, { isLoading: markingInfo }] = useMarkInformationalMutation()
  const [geoResolve, { isLoading: resolving }] = useGeoResolveMutation()
  const [createEvent, { isLoading: saving }] = useCreateEventMutation()

  // ── wizard position ──────────────────────────────────────────────────────────
  const [step, setStep] = useState(0)

  // ── Step 1 — CP Intimation ───────────────────────────────────────────────────
  const [intimMode, setIntimMode] = useState('new') // new | pending
  const [receivedAt, setReceivedAt] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachmentLinks, setAttachmentLinks] = useState([])
  // Dropdown fields live in react-hook-form so they render via the shared
  // RHFSearchableSelectField / RHFMultipleSelectField (own search — same as the
  // reverse-shipment courier filter). Courier selection is held as cpAccountCodes.
  const methods = useForm({ defaultValues: { reason: '', pendingId: '', direction: 'BOTH', impactType: 'TAT_EXTENSION', parents: [], accounts: [] } })
  const { watch, setValue, handleSubmit } = methods
  const reason = watch('reason')
  const pendingId = watch('pendingId')
  const direction = watch('direction')
  const impactType = watch('impactType')
  // an INFORMATIONAL_ONLY event never tags shipments, so the backend ignores the EDD and
  // notification options entirely — hide them rather than let ops set flags that do nothing
  const infoOnly = impactType === 'INFORMATIONAL_ONLY'
  // ── simplified courier selection: parent first, optional account subset ──
  // ALL_PARENTS is a UI sentinel — the payload becomes { allCouriers: true }
  const parentsSel = watch('parents')
  const accountCodes = watch('accounts')
  const allCouriers = (parentsSel || []).includes(ALL_PARENTS)
  const realParents = useMemo(() => (parentsSel || []).filter((p) => p !== ALL_PARENTS), [parentsSel])
  // account narrowing only makes sense for exactly ONE parent — with several, the
  // accounts field could not say which family each pick belongs to
  // stable identity of the selected parent SET — account picks reset when it changes
  const parentsKey = realParents.map((p) => p.trim().toLowerCase()).sort().join('|')
  // distinct parents, deduped case-insensitively (prod holds drifted spellings like
  // "Xpressbees"/"XpressBees" — the backend folds them, so ONE dropdown entry per family)
  const parentOptions = useMemo(() => {
    const seen = new Map()
    couriers.forEach((c) => {
      const p = (c.parent || '').trim()
      if (p && !seen.has(p.toLowerCase())) seen.set(p.toLowerCase(), p)
    })
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
  }, [couriers])
  // accounts of the chosen family — same case-insensitive rule as the backend
  // accounts of EVERY selected family; picks narrow their own parent (codes are unique),
  // parents with no picks stay fully covered
  const accountOptions = useMemo(() => (!allCouriers && realParents.length
    ? dedupeCouriers(couriers).filter((c) => realParents.some((p) => (c.parent || '').trim().toLowerCase() === p.trim().toLowerCase()))
    : []), [couriers, realParents, allCouriers])
  // "Xpressbees · 2 accounts, Delhivery (all accounts)" — shared by summary bar & recap
  const coverageText = useMemo(() => {
    if (allCouriers || !realParents.length) return ''
    const picksByParent = {}
    ;(accountCodes || []).forEach((code) => {
      const par = (couriers.find((c) => c.cpAccountCode === code)?.parent || '').trim().toLowerCase()
      if (par) picksByParent[par] = (picksByParent[par] || 0) + 1
    })
    return realParents.map((p) => {
      const n = picksByParent[p.trim().toLowerCase()]
      return n ? `${p} · ${n} account${n === 1 ? '' : 's'}` : `${p} (all accounts)`
    }).join(', ')
  }, [realParents, accountCodes, couriers, allCouriers])
  // hint shows while the reason still equals what we last auto-suggested (no
  // onChange hook on the shared field, so we derive "manually changed" from this)
  const suggestedRef = useRef(null)
  const autoSuggested = !!reason && reason === suggestedRef.current
  const fileRef = useRef(null)
  const pickupPinRef = useRef(null)
  const dropPinRef = useRef(null)

  const selectedPending = useMemo(() => pending.find((p) => p.id === pendingId), [pending, pendingId])

  // ── event state ──────────────────────────────────────────────────────────────
  const [sourceIntimationId, setSourceIntimationId] = useState(null)
  const [title, setTitle] = useState('')
  const [advisoryRef, setAdvisoryRef] = useState('')
  // direction / impactType are RHF-owned (see methods above)
  // Geography is per leg now — each independently toggleable, at least one required.
  // Both legs are always shown; a leg counts as "used" only if it has a selection
  // (empty = ignored) — no enable toggle, so the user sees both areas at a glance.
  const [pickup, setPickup] = useState(emptyLeg())
  const [drop, setDrop] = useState(emptyLeg())
  const patchPickup = (patch) => setPickup((p) => ({ ...p, ...patch }))
  const patchDrop = (patch) => setDrop((p) => ({ ...p, ...patch }))
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [notifyCustomers, setNotifyCustomers] = useState(false)
  const [bannerMessage, setBannerMessage] = useState('') // sent as bannerMessage → merchant-panel banner notification
  const [merchantVisible, setMerchantVisible] = useState(true)
  const [updateEdd, setUpdateEdd] = useState(false)
  const [eddMode, setEddMode] = useState('extend') // extend (+N days per order) | fixed (one date for all)
  const [eddDays, setEddDays] = useState('3')
  const [revisedEdd, setRevisedEdd] = useState('')

  // Lazy geo lists — only fire the slow endpoint for the tab in use (KCI-018/019).
  const anyLegMode = (m) => pickup.geoType === m || drop.geoType === m
  const { data: states = [], isFetching: statesLoading } = useGeoStatesQuery(undefined, { skip: !anyLegMode(GEO_TYPE.STATE) })
  const { data: cities = [], isFetching: citiesLoading } = useGeoCitiesQuery(undefined, { skip: !anyLegMode(GEO_TYPE.CITY) })

  // ── field-level validation errors ──────────────────────────────────────────────
  const [fieldErr, setFieldErr] = useState({})
  const clearErr = (k) => setFieldErr((p) => (p[k] ? { ...p, [k]: undefined } : p))
  // RHF fields have no onChange hook, so clear their validation errors reactively.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (reason) clearErr('reason') }, [reason])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (pendingId) clearErr('pendingId') }, [pendingId])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (parentsSel?.length) clearErr('parentCourier') }, [parentsSel])
  // account picks belong to one family — changing the (single) parent, adding a
  // second parent, or picking All couriers all invalidate them
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setValue('accounts', []) }, [parentsKey])
  // "All couriers" is exclusive — combining it with named parents is ambiguous
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (allCouriers && realParents.length) setValue('parents', [ALL_PARENTS])
  }, [allCouriers, realParents.length, setValue])
  // continuing from a pending intimation: prefill the parent (new rows carry
  // parentCourier; old account-level rows derive it from their courierCode) — also
  // fixes the old flow where pending mode reached Step 3 with no courier selected
  useEffect(() => {
    if (!selectedPending) return
    const raw = selectedPending.parentCourier
      || couriers.find((c) => c.cpAccountCode === selectedPending.courierCode)?.parent || ''
    if (!raw) return
    if (raw.trim().toLowerCase() === 'all couriers') { setValue('parents', [ALL_PARENTS]); return }
    const match = parentOptions.find((p) => p.toLowerCase() === raw.trim().toLowerCase())
    if (match) setValue('parents', [match])
  }, [selectedPending, parentOptions, couriers, setValue])

  // ── Step 1 behaviours ─────────────────────────────────────────────────────────
  const runSuggest = async () => {
    if (!subject && !body) return
    try {
      const res = await suggestReason({ subject, body }).unwrap()
      if (res?.suggested) { setValue('reason', res.suggested); suggestedRef.current = res.suggested }
    } catch { /* best-effort — silent on failure */ }
  }

  const onPickFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const res = await uploadAttachment(file).unwrap()
      if (res?.fileUrl) setAttachmentLinks((a) => [...a, res.fileUrl])
    } catch (err) { enqueueSnackbar(errMsg(err, 'Attachment upload failed.'), { variant: 'error' }) }
    e.target.value = ''
  }

  const buildIntimationBody = () => ({
    // parent-first contract: the intimation records the family the advisory came from;
    // "All Couriers" is the whitelisted Proship-wide value
    parentCourier: allCouriers ? 'All Couriers' : realParents[0],
    receivedAt: receivedAt ? new Date(receivedAt).toISOString() : undefined,
    subject, body, attachmentLinks,
    reasonCategory: reason,
    reasonCategorySource: autoSuggested ? 'AUTO_SUGGESTED_ACCEPTED' : 'MANUALLY_SELECTED',
    sourceChannel: 'MANUAL_PASTE',
  })

  const validateStepAt = (i) => {
    const e = {}
    if (i === 0) {
      if (intimMode === 'pending') { if (!pendingId) e.pendingId = 'Select a pending intimation to continue.'; return e }
      if (!parentsSel?.length) e.parentCourier = 'Pick one or more courier parents (or All couriers).'
      if (!subject.trim()) e.subject = 'Subject is required.'
      if (!reason) e.reason = 'Pick a reason category.'
    } else if (i === 1) {
      if (!title.trim()) e.title = 'Event title is required.'
    } else if (i === 3) {
      Object.assign(e, geoErrors())
    } else if (i === 4) {
      if (!startDate) e.startDate = 'Start date is required.'
      if (!endDate) e.endDate = 'End date is required.'
      Object.assign(e, eddErrors())
    }
    return e
  }

  // A leg is "used" when it has a real selection (or is explicitly Pan-India).
  const legHasData = (leg) => leg.geoType === GEO_TYPE.PAN
    || (leg.geoType === GEO_TYPE.CITY && leg.cities.length > 0)
    || (leg.geoType === GEO_TYPE.STATE && leg.states.length > 0)
    || (leg.geoType === GEO_TYPE.PIN && parseList(leg.pinText).length > 0)
  // Only rule: at least one leg must be filled (empty legs are simply ignored).
  const geoErrors = () => (legHasData(pickup) || legHasData(drop)
    ? {}
    : { geo: 'Set a pickup or drop area — pick a city, state, pincode list, or Pan-India for at least one.' })

  // EDD mode validation, shared by the step-Next check and the final save.
  const eddErrors = () => {
    // the EDD block is hidden for informational events — a stale checkbox from before the
    // impact type was switched must not block the save
    if (infoOnly || !updateEdd) return {}
    if (eddMode === 'extend') {
      const n = Number(eddDays)
      if (!Number.isInteger(n) || n < 1 || n > 60) return { eddDays: 'eddExtensionDays must be between 1 and 60' }
    } else if (!revisedEdd) {
      return { revisedEdd: 'Pick a fixed revised EDD.' }
    }
    return {}
  }

  const goBack = () => setStep((s) => Math.max(0, s - 1))

  const goNext = async () => {
    const errs = validateStepAt(step)
    setFieldErr(errs)
    if (Object.keys(errs).length) { enqueueSnackbar('Please fix the highlighted field(s).', { variant: 'error' }); return }
    if (step === 0) {
      try {
        const intimation = intimMode === 'pending' ? selectedPending : await createIntimation(buildIntimationBody()).unwrap()
        setSourceIntimationId(intimation?.id || null)
        if (intimation?.reasonCategory) setValue('reason', intimation.reasonCategory)
        setStep(1)
      } catch (err) { enqueueSnackbar(errMsg(err, 'Could not log the intimation.'), { variant: 'error' }) }
      return
    }
    setStep((s) => Math.min(LAST, s + 1))
  }

  const saveInfoOnly = async () => {
    const errs = validateStepAt(0)
    setFieldErr(errs)
    if (Object.keys(errs).length) { enqueueSnackbar('Please fix the highlighted field(s).', { variant: 'error' }); return }
    try {
      const id = intimMode === 'new' ? (await createIntimation(buildIntimationBody()).unwrap())?.id : pendingId
      await markInformational(id).unwrap()
      enqueueSnackbar('Saved for your records — no event was created and no shipments were affected.', { variant: 'success' })
    } catch (err) { enqueueSnackbar(errMsg(err, 'Could not save intimation.'), { variant: 'error' }) }
  }

  // preview the resolved pincode count for one leg (City/State) via /geo/resolve
  const resolveLeg = async (leg, patch) => {
    try {
      const res = await geoResolve({
        states: leg.geoType === GEO_TYPE.STATE ? leg.states : [],
        cities: leg.geoType === GEO_TYPE.CITY ? leg.cities : [],
        excludedPincodes: parseList(leg.excludedText),
      }).unwrap()
      patch({ resolved: res })
    } catch (err) { enqueueSnackbar(errMsg(err, 'Could not resolve pincodes.'), { variant: 'error' }) }
  }

  // Per-leg bulk CSV (single "Pincode" column) — parsed in-browser and merged into THAT
  // leg's pincode list. Lives inside the leg's Pincode-List section, like the old UI.
  const onLegCsv = (patch, existing, kp) => async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      const cells = text.split(/\r?\n/).map((line) => (line.split(',')[0] || '').trim().replace(/^"|"$/g, '')).filter(Boolean)
      const pins = cells.filter((c) => /^\d{6}$/.test(c))
      if (!pins.length) { enqueueSnackbar('No valid 6-digit pincodes found in that CSV.', { variant: 'warning' }); e.target.value = ''; return }
      const before = parseList(existing)
      const merged = [...new Set([...before, ...pins])]
      const skipped = cells.length - pins.length
      patch({ pinText: merged.join(', ') })
      clearErr(`${kp}Pins`)
      enqueueSnackbar(
        `Loaded ${merged.length - before.length} pincode(s) from ${file.name}` +
        (skipped ? ` · ${skipped} non-pincode row(s) skipped` : ''),
        { variant: 'success' },
      )
    } catch { enqueueSnackbar('Could not read the CSV file.', { variant: 'error' }) }
    e.target.value = ''
  }

  const downloadPincodeTemplate = () => {
    const csv = 'Pincode\n110001\n110009\n411045\n'
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a'); a.href = url; a.download = 'kci_pincodes_template.csv'
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url)
  }

  // quick duration presets — end = start + N (KCI-028)
  const setDurationDays = (days) => {
    const start = startDate || new Date().toISOString().slice(0, 10)
    setStartDate(start)
    const d = new Date(start); d.setDate(d.getDate() + days)
    setEndDate(d.toISOString().slice(0, 10))
  }

  const scopeSummary = useMemo(() => {
    const courierText = allCouriers ? 'All couriers'
      : !realParents.length ? 'No courier selected'
        : coverageText
    const legText = (leg) => {
      if (leg.geoType === GEO_TYPE.CITY) return leg.cities.length ? leg.cities.join(' + ') : 'city?'
      if (leg.geoType === GEO_TYPE.STATE) return leg.states.length ? leg.states.join(' + ') : 'state?'
      if (leg.geoType === GEO_TYPE.PIN) return `${parseList(leg.pinText).length} pin`
      return 'Pan-India'
    }
    const geoText = [legHasData(pickup) && `Pickup: ${legText(pickup)}`, legHasData(drop) && `Drop: ${legText(drop)}`].filter(Boolean).join(' · ') || 'No area selected'
    const dirLabel = DIRECTIONS.find((d) => d.value === direction)?.label
    let dateText = ''
    if (startDate && endDate) dateText = `${startDate} – ${endDate}`
    else if (startDate) dateText = startDate
    return [courierText, geoText, dirLabel, dateText].filter(Boolean).join('  ·  ')
  }, [realParents, coverageText, allCouriers, pickup, drop, direction, startDate, endDate])

  const validateEvent = () => {
    const e = {}
    if (!title.trim()) e.title = 'Event title is required.'
    if (!parentsSel?.length) e.parentCourier = 'Pick at least one courier parent in Step 1.'
    Object.assign(e, geoErrors())
    if (!startDate) e.startDate = 'Start date is required.'
    if (!endDate) e.endDate = 'End date is required.'
    Object.assign(e, eddErrors())
    return e
  }

  const buildEventBody = (saveAsDraft) => ({
    title,
    reasonCategory: reason || undefined,
    advisoryReference: advisoryRef || undefined,
    sourceIntimationId: sourceIntimationId || undefined,
    // parent-first contract: scope is derived server-side — accountCodes present =
    // ACCOUNT_LIST, bare parent = PARENT_WIDE, allCouriers = one Proship-wide event
    // ONE event always: a single parent (optionally narrowed to accounts), several
    // parents as one union event (parentCouriers), or Proship-wide (allCouriers)
    ...(allCouriers
      ? { allCouriers: true }
      : { courierSelections: [{
          ...(realParents.length === 1 ? { parentCourier: realParents[0] } : { parentCouriers: realParents }),
          ...(accountCodes?.length ? { accountCodes } : {}),
        }] }),
    direction,
    pickupGeography: legHasData(pickup) ? legBody(pickup) : undefined,
    dropGeography: legHasData(drop) ? legBody(drop) : undefined,
    impactType,
    startDate: dayOnly(startDate),
    endDate: dayOnly(endDate),
    // informational events never tag, so every downstream option is forced off rather than
    // persisted as a flag the backend will silently ignore
    updateCustomEdd: infoOnly ? false : updateEdd,
    // exactly one of the two when the checkbox is on (KCI EDD dual-mode contract)
    revisedEdd: !infoOnly && updateEdd && eddMode === 'fixed' ? dayOnly(revisedEdd) : undefined,
    eddExtensionDays: !infoOnly && updateEdd && eddMode === 'extend' ? Number(eddDays) : undefined,
    customerNotificationsEnabled: infoOnly ? false : notifyCustomers,
    merchantPanelVisibility: infoOnly ? false : merchantVisible,
    bannerMessage: !infoOnly && merchantVisible ? bannerMessage : undefined,
    saveAsDraft,
  })

  const onSave = async (saveAsDraft) => {
    const errs = validateEvent()
    setFieldErr(errs)
    if (Object.keys(errs).length) {
      enqueueSnackbar('Please fix the highlighted field(s).', { variant: 'error' })
      if (errs.title) setStep(1)
      else if (errs.geo) setStep(3)
      else if (errs.startDate || errs.endDate || errs.revisedEdd || errs.eddDays) setStep(4)
      return
    }
    try {
      const res = await createEvent(buildEventBody(saveAsDraft)).unwrap()
      const list = Array.isArray(res) ? res : [res]
      const ids = list.map((e) => e.id).filter(Boolean)
      const totalRejected = list.reduce((n, e) => n + (e.rejectedPincodes?.length || 0), 0)
      enqueueSnackbar(
        `${saveAsDraft ? 'Draft saved' : 'Event created & KCI email triggered'} — ${list.length} event(s): ${ids.join(', ')}. Tagging runs in the background.` +
        (totalRejected ? ` ${totalRejected} pincode(s) rejected as invalid.` : ''),
        { variant: 'success' },
      )
      navigate('/kci/events')
    } catch (err) {
      if (err?.status === 409) enqueueSnackbar(errMsg(err, 'This event overlaps a live KCI event.'), { variant: 'warning' })
      else enqueueSnackbar(errMsg(err, 'Could not create the event.'), { variant: 'error' })
    }
  }


  const courierLabel = (c) => `${c.name || c.cpAccountCode}${c.cpAccountCode && c.name ? ` (${c.cpAccountCode})` : ''}`

  // one geography leg section (rendered only when the leg is enabled) — title +
  // 4-mode selector + inputs; the bulk upload lives inside the Pincode-List mode.
  const renderLeg = (leg, patch, kp, title, subtitle, fileRef, required) => (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.75, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{title}</Typography>
        {!required && <Box component='span' sx={{ fontSize: 9.5, fontWeight: 700, color: '#0f766e', bgcolor: '#e4f3f1', px: 0.7, py: '1px', borderRadius: '5px', textTransform: 'uppercase', letterSpacing: '.03em' }}>Optional</Box>}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 1.5 }}>{subtitle}</Typography>
      <ToggleButtonGroup size='small' exclusive value={leg.geoType} onChange={(ev, v) => { if (v) patch({ geoType: v, resolved: null }) }} sx={{ ...toggleGroupSx, mb: 2 }}>
        <ToggleButton value={GEO_TYPE.CITY}>City</ToggleButton>
        <ToggleButton value={GEO_TYPE.STATE}>State</ToggleButton>
        <ToggleButton value={GEO_TYPE.PIN}>Pincode List</ToggleButton>
        <ToggleButton value={GEO_TYPE.PAN}>Pan-India</ToggleButton>
      </ToggleButtonGroup>

      {leg.geoType === GEO_TYPE.CITY && (
        <Autocomplete multiple size='small' fullWidth options={cities} value={leg.cities} loading={citiesLoading}
          loadingText='Loading cities…' onChange={(ev, v) => { patch({ cities: v, resolved: null }); clearErr(`${kp}Cities`) }}
          renderOption={sepOption((o) => o)} noOptionsText={citiesLoading ? 'Loading cities…' : 'No cities found'}
          renderInput={(p) => <TextField {...p} label={`Cities${required ? ' *' : ''}`} placeholder='Search and select one or more' error={!!fieldErr[`${kp}Cities`]}
            helperText={fieldErr[`${kp}Cities`] || (citiesLoading ? 'Loading the city list (can take up to a minute)…' : '')}
            InputProps={{ ...p.InputProps, endAdornment: <>{citiesLoading ? <CircularProgress size={16} /> : null}{p.InputProps.endAdornment}</> }} />} />
      )}
      {leg.geoType === GEO_TYPE.STATE && (
        <Autocomplete multiple size='small' fullWidth options={states} value={leg.states} loading={statesLoading}
          loadingText='Loading states…' onChange={(ev, v) => { patch({ states: v, resolved: null }); clearErr(`${kp}States`) }}
          renderOption={sepOption((o) => o)} noOptionsText={statesLoading ? 'Loading states…' : 'No states found'}
          renderInput={(p) => <TextField {...p} label={`States${required ? ' *' : ''}`} placeholder='Search and select one or more' error={!!fieldErr[`${kp}States`]}
            helperText={fieldErr[`${kp}States`] || (statesLoading ? 'Loading the state list (can take up to a minute)…' : '')}
            InputProps={{ ...p.InputProps, endAdornment: <>{statesLoading ? <CircularProgress size={16} /> : null}{p.InputProps.endAdornment}</> }} />} />
      )}
      {(leg.geoType === GEO_TYPE.CITY || leg.geoType === GEO_TYPE.STATE) && (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <TextField size='small' fullWidth label='Exclude pincodes (optional, comma-separated)' value={leg.excludedText} onChange={(ev) => patch({ excludedText: ev.target.value })} placeholder='e.g. 400001, 400002' />
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Button variant='outlined' size='small' sx={resetBtnSx} onClick={() => resolveLeg(leg, patch)} disabled={resolving || (!leg.cities.length && !leg.states.length)}>
              {resolving ? 'Resolving…' : 'Preview pincode count'}
            </Button>
            {leg.resolved && <Typography variant='body2'><b>{leg.resolved.pincodeCount?.toLocaleString?.() ?? leg.resolved.pincodeCount}</b> pincodes resolved</Typography>}
          </Stack>
        </Stack>
      )}
      {leg.geoType === GEO_TYPE.PIN && (
        <Stack spacing={1.5}>
          <TextField size='small' fullWidth label='Pincodes' multiline minRows={2}
            value={leg.pinText} onChange={(ev) => { patch({ pinText: ev.target.value }); clearErr(`${kp}Pins`) }} placeholder='e.g. 110001, 110009'
            error={!!fieldErr[`${kp}Pins`]} helperText={fieldErr[`${kp}Pins`] || 'Type or paste pincodes separated by commas'} />
          <input ref={fileRef} type='file' hidden accept='.csv,text/csv' onChange={onLegCsv(patch, leg.pinText, kp)} />
          <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap>
            <Button variant='outlined' size='small' sx={resetBtnSx} startIcon={<UploadFileOutlinedIcon />} onClick={() => fileRef.current?.click()}>Bulk upload (CSV)</Button>
            <Button size='small' variant='text' onClick={downloadPincodeTemplate} sx={{ textTransform: 'none' }}>Download sample template</Button>
            <Typography variant='caption' color='text.secondary'>Single “Pincode” column · parsed in your browser and added above.</Typography>
          </Stack>
        </Stack>
      )}
      {leg.geoType === GEO_TYPE.PAN && (
        <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'rgba(32,101,209,.06)', border: '1px solid rgba(32,101,209,.18)', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: BLUE, mt: '2px', flex: '0 0 auto' }} />
          <Typography sx={{ fontSize: 12.5, color: NAVY }}>This leg matches <b>every pincode</b> nationwide within the courier scope above.</Typography>
        </Box>
      )}
    </Box>
  )
  const meta = STEPS[step]

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(() => {})}>
    <Box sx={pageContainerSx}>
      <PageTitle>KCI Management · Create KCI Event</PageTitle>

      {/* How it works */}
      <Box sx={helpBannerSx}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: BLUE, mt: '2px', flex: '0 0 auto' }} />
        <Typography sx={helpTextSx}>
          <strong>How it works:</strong> Complete each step in order — log the courier communication, then set courier scope, geography, duration and notifications. On save, KCI auto-tags every matching shipment (or keep it as a draft).
        </Typography>
      </Box>

      <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
        {/* Stepper sidebar */}
        <Grid item xs={12} md={2.5}>
          <Box sx={sidebarCardSx}>
            <Stepper orientation='vertical' activeStep={step} sx={stepperSx}>
              {STEPS.map((s) => (<Step key={s.key}><StepLabel>{s.label}</StepLabel></Step>))}
            </Stepper>
            {step > 0 && <Typography sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${BORDER}`, fontSize: 11, color: SUBTLE, lineHeight: 1.55 }}>{scopeSummary}</Typography>}
          </Box>
        </Grid>

        {/* Form area */}
        <Grid item xs={12} md={9.5}>
          <Box sx={formCardSx}>
            <Typography sx={stepTitleSx}>{step + 1}. {meta.title}</Typography>
          {/* ── Step 1 — CP Intimation ── */}
          {step === 0 && (
            <>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: MUTED, mb: 0.75 }}>How are you logging this?</Typography>
              <ToggleButtonGroup size='small' exclusive value={intimMode} onChange={(e, v) => v && setIntimMode(v)} sx={toggleGroupSx}>
                <ToggleButton value='new'>Log a new intimation</ToggleButton>
                <ToggleButton value='pending'>Continue from a pending intimation</ToggleButton>
              </ToggleButtonGroup>
              <Divider sx={{ my: 2 }} />

              {intimMode === 'new' ? (
                <Stack spacing={2}>
                  <Box sx={ROW2}>
                    <Box>
                      <RHFMultipleSelectField name='parents' label='Courier parent(s) *' search width='100%'
                        outputFormat='array' placeholder='Search parents, or pick All couriers'
                        sx={{ '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' } }}
                        options={[{ value: ALL_PARENTS, label: 'All couriers' },
                          ...parentOptions.map((p) => ({ value: p, label: p }))]} />
                      {fieldErr.parentCourier && <Typography sx={{ fontSize: 11, mt: 0.4, color: '#d32f2f' }}>{fieldErr.parentCourier}</Typography>}
                      {realParents.length > 1 && (
                        <Typography sx={{ fontSize: 11, mt: 0.4, color: MUTED }}>
                          One KCI event will cover all {realParents.length} selected parents.
                        </Typography>
                      )}
                    </Box>
                    <TextField size='small' type='datetime-local' label='Received date/time' fullWidth
                      InputLabelProps={{ shrink: true }} value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
                  </Box>
                  {/* optional narrowing: picks narrow THEIR parent; a selected parent with
                      no picks stays fully covered (whole-parent) */}
                  {!allCouriers && realParents.length > 0 && (
                    <Box>
                      <RHFMultipleSelectField name='accounts' label='Courier accounts (optional)' search width='100%'
                        outputFormat='array' placeholder='Leave blank to cover ALL accounts of the selected parent(s)'
                        sx={{ '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' } }}
                        options={accountOptions.map((c) => ({ value: c.cpAccountCode,
                          label: realParents.length > 1 ? `${(c.parent || '').trim()} · ${courierLabel(c)}` : courierLabel(c) }))} />
                      <Typography sx={{ fontSize: 11, mt: 0.4, color: MUTED }}>
                        {accountCodes?.length ? `Covered: ${coverageText}.`
                          : `All ${accountOptions.length} accounts under ${realParents.join(', ')} will be covered.`}
                      </Typography>
                    </Box>
                  )}
                  <TextField size='small' label='Subject *' fullWidth value={subject} onChange={(e) => { setSubject(e.target.value); clearErr('subject') }} onBlur={runSuggest}
                    error={!!fieldErr.subject} helperText={fieldErr.subject} />
                  <TextField size='small' label='Body' fullWidth multiline minRows={3} maxRows={7}
                    value={body} onChange={(e) => setBody(e.target.value)} onBlur={runSuggest}
                    placeholder="Paste the courier's exact message here."
                    helperText="Kept in ProShip for internal reference only — never emailed or shared outside ProShip." />

                  {/* Reason category + Attachment share one row */}
                  <Box sx={{ ...ROW2, alignItems: 'flex-start' }}>
                    <Box>
                      <RHFSearchableSelectField name='reason' label='Reason category *' fullWidth placeholder='Select a reason'
                        options={REASON_CATEGORIES.map((r) => ({ value: r.value, label: r.label }))} />
                      {(fieldErr.reason || autoSuggested) && (
                        <Typography sx={{ fontSize: 11, mt: 0.4, color: fieldErr.reason ? '#d32f2f' : MUTED }}>
                          {fieldErr.reason || 'Auto-suggested — change if needed.'}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <input ref={fileRef} type='file' hidden accept='.pdf,.docx,.png,.jpg,.jpeg,.eml,.msg' onChange={onPickFile} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', minHeight: 44,
                        border: '1px dashed #CBD5E1', borderRadius: '10px', px: 1.5, py: 0.75, bgcolor: '#F8FAFC' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <UploadFileOutlinedIcon sx={{ color: BLUE, fontSize: 18 }} />
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY }}>Attachment</Typography>
                            <Typography sx={{ fontSize: 10.5, color: MUTED }}>PDF, DOCX, PNG, JPG, EML, MSG · max 10 MB</Typography>
                          </Box>
                        </Box>
                        <Button variant='outlined' size='small' sx={resetBtnSx} startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                          onClick={() => fileRef.current?.click()} disabled={uploading}>
                          {uploading ? 'Uploading…' : 'Upload'}
                        </Button>
                      </Box>
                      {attachmentLinks.length > 0 && (
                        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
                          {attachmentLinks.map((u, i) => (
                            <Chip key={i} size='small' label={u.split('/').pop()} onDelete={() => setAttachmentLinks((a) => a.filter((_, j) => j !== i))} sx={{ maxWidth: '100%' }} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <RHFSearchableSelectField name='pendingId' label='Pending intimation *' fullWidth placeholder='Select a pending intimation'
                      options={pending.length === 0
                        ? [{ value: '', label: 'No pending intimations', disabled: true }]
                        : pending.map((p) => ({ value: p.id, label: `${p.id} — ${p.courierCode} — ${p.subject}` }))} />
                    {fieldErr.pendingId && <Typography sx={{ fontSize: 11, mt: 0.4, color: '#d32f2f' }}>{fieldErr.pendingId}</Typography>}
                  </Box>
                  {selectedPending && (
                    <Box sx={{ p: 1.75, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                      <Typography variant='caption' color='text.secondary'>
                        <b>From:</b> {selectedPending.parentCourier || selectedPending.courierCode} &nbsp; <b>Reason:</b> {reasonLabel(selectedPending.reasonCategory)}
                      </Typography>
                      <Typography variant='body2' sx={{ mt: 1 }}>{selectedPending.body || '—'}</Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </>
          )}

          {/* ── Step 2 — Event Details ── */}
          {step === 1 && (
            <Stack spacing={2}>
              <Box sx={ROW2}>
                <TextField size='small' label='Event title *' fullWidth value={title} onChange={(e) => { setTitle(e.target.value); clearErr('title') }}
                  error={!!fieldErr.title} helperText={fieldErr.title} />
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40 }}>
                  <Typography sx={{ fontSize: 10, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Reason category (from Step 1)</Typography>
                  <Box sx={{ mt: 0.5 }}><Chip size='small' label={reasonLabel(reason)} sx={{ bgcolor: `${reasonColor(reason)}22`, color: NAVY, fontWeight: 600 }} /></Box>
                </Box>
              </Box>
              <Box sx={ROW2}>
                <TextField size='small' label='Advisory reference no.' fullWidth value={advisoryRef} onChange={(e) => setAdvisoryRef(e.target.value)} placeholder='Courier advisory ID (optional)' />
                <TextField size='small' label='Linked CP Intimation' fullWidth value={sourceIntimationId || '—'} disabled />
              </Box>
            </Stack>
          )}

          {/* ── Step 3 — Scope, Direction & Impact ── */}
          {step === 2 && (
            <>
              {/* courier selection lives ENTIRELY on step 1 now (parent-first flow) —
                  this step only sets which shipments the event applies to */}
              <Box sx={{ mb: 2, p: 1.25, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F5' }}>
                <Typography variant='caption' color='text.secondary'>Applies to</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: NAVY }}>
                  {allCouriers ? 'All couriers'
                    : !realParents.length ? 'No courier selected — go back to Step 1'
                      : coverageText}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY, mb: 0.75 }}>Shipment direction *</Typography>
                <RHFSearchableSelectField name='direction' label='' fullWidth placeholder='Select direction'
                  options={DIRECTIONS.map((d) => ({ value: d.value, label: d.label }))} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY, mb: 0.75 }}>Impact type *</Typography>
                <RadioGroup value={impactType} onChange={(e) => setValue('impactType', e.target.value)} sx={{ gap: 1 }}>
                  {IMPACT_TYPES.map((i) => {
                    const sel = impactType === i.value
                    return (
                      <FormControlLabel key={i.value} value={i.value}
                        control={<Radio size='small' sx={{ py: 0, mt: 0.25, alignSelf: 'flex-start' }} />}
                        sx={{ width: '100%', alignItems: 'flex-start', m: 0, p: 1.25, borderRadius: '10px', border: '1px solid', borderColor: sel ? BLUE : '#E2E8F0', bgcolor: sel ? '#F1F6FE' : '#fff', transition: 'border-color .15s, background-color .15s', '& .MuiFormControlLabel-label': { flex: 1 } }}
                        label={(
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{i.label}</Typography>
                            {i.desc && <Typography sx={{ fontSize: 11.5, color: MUTED, mt: '2px', lineHeight: 1.45 }}>{i.desc}</Typography>}
                          </Box>
                        )} />
                    )
                  })}
                </RadioGroup>
              </Box>
            </>
          )}

          {/* ── Step 4 — Geography (per leg: pickup / drop) ── */}
          {step === 3 && (
            <>
              {fieldErr.geo && <Typography sx={{ fontSize: 12, color: '#d32f2f', mb: 1.5 }}>{fieldErr.geo}</Typography>}
              <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '8px', bgcolor: '#EEF4FE', border: '1px solid #CFE0F7' }}>
                <Typography sx={{ fontSize: 12, color: '#1E40AF', lineHeight: 1.5 }}>
                  If both pickup and drop geographies are listed below, KCI tagging applies when <b>either</b> one matches the order (OR condition, not AND — a single match is sufficient).
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {renderLeg(pickup, patchPickup, 'pickup', 'Affected pickup area', "Matches the order's pickup pincode", pickupPinRef, !legHasData(drop))}
                {renderLeg(drop, patchDrop, 'drop', 'Affected drop area', "Matches the order's delivery pincode", dropPinRef, !legHasData(pickup))}
              </Box>
            </>
          )}

          {/* ── Step 5 — Duration ── */}
          {step === 4 && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                <TextField size='small' type='date' label='Start date *' InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => { setStartDate(e.target.value); clearErr('startDate') }}
                  error={!!fieldErr.startDate} helperText={fieldErr.startDate} sx={{ width: 190 }} />
                <TextField size='small' type='date' label='End date *' InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => { setEndDate(e.target.value); clearErr('endDate') }}
                  error={!!fieldErr.endDate} helperText={fieldErr.endDate} sx={{ width: 190 }} />
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ pt: 0.5 }}>
                  <Button size='small' variant='outlined' sx={resetBtnSx} onClick={() => setDurationDays(1)}>1 day</Button>
                  <Button size='small' variant='outlined' sx={resetBtnSx} onClick={() => setDurationDays(2)}>2 days</Button>
                  <Button size='small' variant='outlined' sx={resetBtnSx} onClick={() => setDurationDays(3)}>3 days</Button>
                </Stack>
              </Box>
              <Divider sx={{ my: 2.5 }} />
              {infoOnly ? (
                <Alert severity='info' sx={{ borderRadius: '10px' }}>
                  An <b>Informational only</b> event never tags shipments, so there is nothing to
                  apply a revised EDD to. Change the impact type in step 3 to enable this.
                </Alert>
              ) : (
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', p: 2, bgcolor: '#FBFCFE' }}>
                  <FormControlLabel sx={{ m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { flex: 1 } }}
                    control={<Checkbox checked={updateEdd} onChange={(e) => setUpdateEdd(e.target.checked)} sx={{ py: 0, mt: -0.25, alignSelf: 'flex-start' }} />}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>Update custom EDD on impacted shipments</Typography>
                        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: '2px', lineHeight: 1.5 }}>Custom EDD is currently for logging purposes only and does not impact any performance metric in ProShip.</Typography>
                      </Box>
                    )} />

                  {updateEdd && (
                    <RadioGroup value={eddMode} onChange={(e) => { setEddMode(e.target.value); clearErr('eddDays'); clearErr('revisedEdd') }} sx={{ mt: 1.75, gap: 1 }}>
                      {[
                        { value: 'extend', title: "Extend each order's EDD by a set number of days", desc: 'Every impacted order is shifted from its own system EDD.' },
                        { value: 'fixed', title: 'Set one fixed revised EDD for all orders', desc: 'Every impacted order gets the same delivery date.' },
                      ].map((m) => {
                        const sel = eddMode === m.value
                        return (
                          <FormControlLabel key={m.value} value={m.value}
                            control={<Radio size='small' sx={{ py: 0, mt: 0.25, alignSelf: 'flex-start' }} />}
                            sx={{ width: '100%', alignItems: 'flex-start', m: 0, p: 1.25, borderRadius: '10px', border: '1px solid', borderColor: sel ? BLUE : '#E2E8F0', bgcolor: sel ? '#F1F6FE' : '#fff', transition: 'border-color .15s, background-color .15s', '& .MuiFormControlLabel-label': { flex: 1 } }}
                            label={(
                              <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{m.title}</Typography>
                                <Typography sx={{ fontSize: 11.5, color: MUTED, mt: '2px', lineHeight: 1.45 }}>{m.desc}</Typography>
                                {sel && m.value === 'extend' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }} onClick={(e) => e.stopPropagation()}>
                                    <TextField size='small' type='number' value={eddDays} onChange={(e) => { setEddDays(e.target.value); clearErr('eddDays') }}
                                      inputProps={{ min: 1, max: 60 }} error={!!fieldErr.eddDays} helperText={fieldErr.eddDays} sx={{ width: 100 }} />
                                    <Typography sx={{ fontSize: 13, color: NAVY }}>day(s)</Typography>
                                  </Box>
                                )}
                                {sel && m.value === 'fixed' && (
                                  <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                                    <TextField size='small' type='date' InputLabelProps={{ shrink: true }} value={revisedEdd} onChange={(e) => { setRevisedEdd(e.target.value); clearErr('revisedEdd') }}
                                      error={!!fieldErr.revisedEdd} helperText={fieldErr.revisedEdd} sx={{ width: 190 }} />
                                  </Box>
                                )}
                              </Box>
                            )} />
                        )
                      })}
                    </RadioGroup>
                  )}
                </Box>
              )}
            </>
          )}

          {/* ── Step 6 — Notifications ── */}
          {step === 5 && (
            <Stack spacing={1.5}>
              {infoOnly && (
                <Alert severity='info' sx={{ borderRadius: '10px' }}>
                  An <b>Informational only</b> event never tags shipments, so there are no impacted
                  merchants or customers to notify and no panel banner to target. The internal ops
                  team is still emailed when the event goes live.
                </Alert>
              )}
              {!infoOnly && (
                <>
                  <FormControlLabel sx={{ m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { flex: 1 } }}
                    control={<Checkbox checked={notifyCustomers} onChange={(e) => setNotifyCustomers(e.target.checked)} sx={{ py: 0, mt: -0.25, alignSelf: 'flex-start' }} />}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>Email impacted end customers</Typography>
                        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: '2px', lineHeight: 1.5 }}>Only where the merchant has KCI notifications enabled. WhatsApp &amp; SMS are not supported currently.</Typography>
                      </Box>
                    )} />
                  <FormControlLabel sx={{ m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { flex: 1 } }}
                    control={<Checkbox checked={merchantVisible} onChange={(e) => setMerchantVisible(e.target.checked)} sx={{ py: 0, mt: -0.25, alignSelf: 'flex-start' }} />}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>Display this event on merchant panels — moving header + push notification</Typography>
                        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: '2px', lineHeight: 1.5 }}>Merchants shipping within the impacted scope get a moving header AND a panel push notification naming the impacted courier partner(s), with a link to the impacted pincode list. The message below is used in both — leave it blank for the reason category's default.</Typography>
                      </Box>
                    )} />
                </>
              )}
              {/* bannerMessage is the text of THAT panel banner — it belongs to this checkbox,
                  not to the customer-email one it used to sit under */}
              {!infoOnly && merchantVisible && (
                // 42px = the checkbox control column (24px icon + 9px padding each side), so the
                // field lines up under the checkbox LABEL as its child at every breakpoint
                <Box sx={{ pl: '42px' }}>
                  <TextField size='small' fullWidth label='Banner message' multiline minRows={2} value={bannerMessage} onChange={(e) => setBannerMessage(e.target.value)}
                    placeholder='Deliveries in your area may be delayed due to…' />
                </Box>
              )}
            </Stack>
          )}

          {/* ── footer — inside the card ── */}
          <Divider sx={{ mt: 2.5, mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Stack direction='row' spacing={1.5}>
              <Button variant='outlined' sx={resetBtnSx} onClick={() => navigate('/kci/events')}>Cancel</Button>
              {step > 0 && <Button variant='outlined' sx={resetBtnSx} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} onClick={goBack}>Back</Button>}
            </Stack>
            <Stack direction='row' spacing={1.5}>
              {step === 0 && (
                <Tooltip title="Use this when the courier's message is only for your information — like a holiday calendar or a 'service back to normal' update — and there's no delivery delay to track. We'll keep it in your records, but it won't create an event or affect any shipments.">
                  <span>
                    <Button variant='outlined' sx={infoOnlyBtnSx} onClick={saveInfoOnly} disabled={markingInfo || loggingIntimation}
                      startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}>
                      Just log it — no event needed
                    </Button>
                  </span>
                </Tooltip>
              )}
              {step < LAST && (
                <Button variant='contained' sx={gradientBtnSx} endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />} onClick={goNext} disabled={loggingIntimation}>
                  {step === 0 ? (loggingIntimation ? 'Logging…' : 'Continue') : 'Next'}
                </Button>
              )}
              {step === LAST && (
                <>
                  <Button variant='outlined' sx={resetBtnSx} onClick={() => onSave(true)} disabled={saving}>Save as draft</Button>
                  <Button variant='contained' sx={gradientBtnSx} onClick={() => onSave(false)} disabled={saving}>{saving ? 'Saving…' : 'Save & trigger KCI email'}</Button>
                </>
              )}
            </Stack>
          </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
    </FormProvider>
  )
}
